/*eslint max-lines-per-function: ["error", 250]*/
import React, { useCallback, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { ClientSurveySearch } from '../../components/ClientSurveys/ClientSurveySearch';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { JobConfigInfo } from '../../components/JobConfig/JobConfigInfo';
import { JobTypeEnum } from '../../../common/pokCore/validation/enums';

enum ClientSurveysTabs {
  WAITING_TO_SEND = 'waitingToSend',
  WAITING_FOR_ANSWER = 'waitingForAnswer',
  EXPIRED_WITHOUT_REMINDER = 'expiredWithoutReminder',
  ALL = 'all',
}

export const ClientSurveysView: React.FC = () => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('administrationLng');
  const lang = pok.getLanguage();
  const [refresh, setRefresh] = useState(false);

  const getWaitingToSendData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.clientSurveys.getAllWaitingToSendByPage(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      )) as GridGetterFunction,
    [lang, pok.clientSurveys, t, tk],
  );

  const getWaitingToSendCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.clientSurveys.getAllWaitingToSendCount(filterText, filterColumns),
    [pok.clientSurveys],
  );

  const getWaitingForAnswerData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.clientSurveys.getAllWaitingForAnswerByPage(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      )) as GridGetterFunction,
    [lang, pok.clientSurveys, t, tk],
  );

  const getWaitingForAnswerCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.clientSurveys.getAllWaitingForAnswerCount(filterText, filterColumns),
    [pok.clientSurveys],
  );

  const getAllData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.clientSurveys.getAllByPage(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      )) as GridGetterFunction,
    [lang, pok.clientSurveys, t, tk],
  );

  const getAllCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.clientSurveys.getAllCount(filterText, filterColumns),
    [pok.clientSurveys],
  );

  const handleRefresh = useCallback(() => setRefresh(r => !r), []);

  const getExpiredWithoutReminderData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.clientSurveys.getAllExpiredWithoutReminderByPage(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        setRefresh,
      )) as GridGetterFunction,
    [lang, pok.clientSurveys, t, tk, setRefresh],
  );

  const getExpiredWithoutReminderCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.clientSurveys.getAllExpiredWithoutReminderCount(
        filterText,
        filterColumns,
      ),
    [pok.clientSurveys],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.clientSurvey.clientSurveys)}
        </Card.Title>
        <TabsHistory
          activeKey={ClientSurveysTabs.WAITING_TO_SEND}
          id="clientSurveys"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={ClientSurveysTabs.WAITING_TO_SEND}
            title={t(tk.clientSurvey.waitingToSend)}
            className="m-3"
          >
            <ClientSurveySearch
              getData={getWaitingToSendData}
              getCount={getWaitingToSendCount}
            />
            <JobConfigInfo
              items={[
                {
                  jobType: JobTypeEnum.GenerateSurveyForClients,
                  nextStartLabel: t(
                    tk.clientSurvey.nextStartGenerateClientSurvey,
                  ),
                  frequencyLabel: t(tk.clientSurvey.frequency),
                },
                {
                  jobType: JobTypeEnum.SendSurveyToClients,
                  nextStartLabel: t(
                    tk.clientSurvey.nextStartSendToClientSurvey,
                  ),
                  frequencyLabel: t(tk.clientSurvey.frequency),
                },
              ]}
            />
          </Tab>
          <Tab
            eventKey={ClientSurveysTabs.WAITING_FOR_ANSWER}
            title={t(tk.clientSurvey.waitingForAnswer)}
            className="m-3"
          >
            <ClientSurveySearch
              getData={getWaitingForAnswerData}
              getCount={getWaitingForAnswerCount}
            />
          </Tab>
          <Tab
            eventKey={ClientSurveysTabs.EXPIRED_WITHOUT_REMINDER}
            title={t(tk.clientSurvey.expiredWithoutReminder)}
            className="m-3"
          >
            <ClientSurveySearch
              getData={getExpiredWithoutReminderData}
              getCount={getExpiredWithoutReminderCount}
              handleRefresh={handleRefresh}
              withRepeat={true}
              refresh={refresh}
            />
          </Tab>
          <Tab
            eventKey={ClientSurveysTabs.ALL}
            title={t(tk.clientSurvey.all)}
            className="m-3"
          >
            <ClientSurveySearch getData={getAllData} getCount={getAllCount} />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
